import { useMemo, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, AlertCircle, Info } from 'lucide-react';
import type {
  CreateQuotaReq,
  QuotaSelectionOperator,
  QuotaSelectorObject,
  Resource,
} from '@/api/types';
import Section from './form/Section';
import Field from './form/Field';
import { inputClass, selectClass } from './form/styles';
import { cn } from '@/lib/utils';
import { parseMemoryBytes } from '@/lib/resource-helpers';

const SELECTOR_OBJECTS: QuotaSelectorObject[] = [
  'User',
  'Project',
  'Department',
  'HPC Application',
];

// The selector_object label drives this hint. Mirrors the tooltip text on
// the legacy /createquotaform template — concrete examples beat abstract
// descriptions of regex on a form people fill in once a quarter.
const SELECTOR_HINTS: Record<QuotaSelectorObject, string> = {
  User: 'e.g. .* (any user), ^alice$ (just alice), ^alice$|^bob$ (alice or bob)',
  Project: 'e.g. .* (any project), ^proj-prod$, ^proj-(dev|test)$',
  Department: 'e.g. .* (any department), ^research$',
  'HPC Application': 'e.g. .* (any cluster ID), ^cluster-001$',
};

export interface QuotaFormValues {
  name: string;
  description: string;
  selectorObject: QuotaSelectorObject;
  selectorOperator: QuotaSelectionOperator;
  selectorRegex: string;
  resource: string;
  quota: string;
}

interface QuotaFormProps {
  mode: 'create' | 'modify';
  values: QuotaFormValues;
  onChange: (next: QuotaFormValues) => void;
  resources: Resource[];
  resourcesLoading: boolean;
  onSubmit: (req: CreateQuotaReq) => void;
  submitting: boolean;
  submitError: string | null;
  cancelHref: string;
}

export default function QuotaForm({
  mode,
  values,
  onChange,
  resources,
  resourcesLoading,
  onSubmit,
  submitting,
  submitError,
  cancelHref,
}: QuotaFormProps) {
  const isCreate = mode === 'create';

  const set = <K extends keyof QuotaFormValues>(
    key: K,
    val: QuotaFormValues[K],
  ) => {
    onChange({ ...values, [key]: val });
  };

  const selectedResource = useMemo(
    () => resources.find((r) => r.name === values.resource),
    [resources, values.resource],
  );

  // Per-resource-type help / validation for the Quota numeric value.
  // Mirrors the server-side parsing in
  // internal/db/quota/quotaDB.go:validateQuota — Memory accepts human
  // units and is byte-converted; Double parses as float; everything else
  // parses as a non-negative integer. String is non-allocatable so the
  // server would silently accept the quota but never apply it.
  const quotaValueState = useMemo<{
    error?: string;
    hint?: string;
    okHint?: string;
  }>(() => {
    if (!values.resource) return {};
    if (values.quota.trim() === '') return {};
    const t = selectedResource?.type;
    const raw = values.quota.trim();
    if (t === 'Memory') {
      const bytes = parseMemoryBytes(raw);
      if (bytes === null)
        return { error: 'Expected a value like 1GB, 512MiB, or 1024' };
      return {
        okHint: `≈ ${bytes.toLocaleString()} bytes`,
      };
    }
    if (t === 'Double') {
      const n = Number(raw);
      if (!Number.isFinite(n)) return { error: 'Must be a number' };
      if (n < 0) return { error: 'Must be ≥ 0' };
      return { okHint: `Will be stored as ${n}` };
    }
    const n = Number(raw);
    if (!Number.isInteger(n)) return { error: 'Must be a whole number' };
    if (n < 0) return { error: 'Must be ≥ 0' };
    return { okHint: `Will be stored as ${n}` };
  }, [selectedResource, values.quota, values.resource]);

  const nameError = useMemo<string | null>(() => {
    if (!isCreate) return null;
    const n = values.name;
    if (!n) return null; // surfaced as required, not as an error
    if (/\s/.test(n)) return 'Name cannot contain spaces';
    if (n.length > 255) return 'Name is too long (max 255 characters)';
    return null;
  }, [isCreate, values.name]);

  const regexError = useMemo<string | null>(() => {
    const r = values.selectorRegex;
    if (!r) return null;
    try {
      new RegExp(r);
      return null;
    } catch (err) {
      return `Invalid regex: ${err instanceof Error ? err.message : 'unknown error'}`;
    }
  }, [values.selectorRegex]);

  const canSubmit =
    !submitting &&
    values.name.trim() !== '' &&
    values.resource !== '' &&
    values.selectorRegex.trim() !== '' &&
    values.quota.trim() !== '' &&
    !nameError &&
    !regexError &&
    !quotaValueState.error;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      name: values.name.trim(),
      description: values.description.trim(),
      selector_object: values.selectorObject,
      selector_regex: values.selectorRegex.trim(),
      selection_operator: values.selectorOperator,
      resource: values.resource,
      comparator: '<=',
      quota: values.quota.trim(),
    });
  };

  const stringSelected = selectedResource?.type === 'String';

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <Section
        title="1. Identity"
        description="A short name and a one-line description of what this quota restricts."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field
            label="Quota name"
            htmlFor="quota-name"
            required
            error={nameError}
            help={
              isCreate
                ? 'No spaces. The name is the quota ID and cannot be changed later.'
                : 'Names cannot be changed after creation.'
            }
          >
            <input
              id="quota-name"
              type="text"
              value={values.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="prod-users-cap"
              autoFocus={isCreate}
              autoComplete="off"
              spellCheck={false}
              readOnly={!isCreate}
              className={cn(
                inputClass(!!nameError),
                !isCreate && 'bg-muted/40 cursor-not-allowed',
              )}
              disabled={submitting}
            />
          </Field>
          <Field
            label="Description"
            htmlFor="quota-description"
            help="Optional. Short summary shown in the quota list."
          >
            <input
              id="quota-description"
              type="text"
              value={values.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Cap each prod user at 8 concurrent jobs"
              className={inputClass(false)}
              disabled={submitting}
            />
          </Field>
        </div>
      </Section>

      <Section
        title="2. Application selection"
        description="Pick whose requests this quota applies to."
      >
        <div className="grid gap-4 md:grid-cols-3">
          <Field
            label="Selection operator"
            htmlFor="selector-operator"
            help={
              values.selectorOperator === 1
                ? 'Limit applies to each matching value independently.'
                : 'Limit applies to the sum across all matching values.'
            }
          >
            <select
              id="selector-operator"
              value={values.selectorOperator}
              onChange={(e) =>
                set(
                  'selectorOperator',
                  Number(e.target.value) as QuotaSelectionOperator,
                )
              }
              className={selectClass(false)}
              disabled={submitting}
            >
              <option value={0}>For all</option>
              <option value={1}>For each</option>
            </select>
          </Field>
          <Field
            label="Selection object"
            htmlFor="selector-object"
            help="What field of the request the regex below is matched against."
          >
            <select
              id="selector-object"
              value={values.selectorObject}
              onChange={(e) =>
                set('selectorObject', e.target.value as QuotaSelectorObject)
              }
              className={selectClass(false)}
              disabled={submitting}
            >
              {SELECTOR_OBJECTS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Matching expression"
            htmlFor="selector-regex"
            required
            error={regexError}
            help={SELECTOR_HINTS[values.selectorObject]}
          >
            <input
              id="selector-regex"
              type="text"
              value={values.selectorRegex}
              onChange={(e) => set('selectorRegex', e.target.value)}
              placeholder=".*"
              className={cn(inputClass(!!regexError), 'font-mono text-xs')}
              spellCheck={false}
              disabled={submitting}
            />
          </Field>
        </div>
      </Section>

      <Section
        title="3. Restriction rule"
        description="The resource and limit this quota enforces."
      >
        <div className="grid gap-4 md:grid-cols-[2fr_1fr_2fr]">
          <Field
            label="Resource"
            htmlFor="quota-resource"
            required
            help={
              resourcesLoading
                ? 'Loading resources…'
                : selectedResource
                  ? `Selected type: ${selectedResource.type}`
                  : 'The resource this quota will count against.'
            }
          >
            <select
              id="quota-resource"
              value={values.resource}
              onChange={(e) => set('resource', e.target.value)}
              className={selectClass(false)}
              disabled={submitting || !isCreate}
            >
              <option value="" disabled>
                {resourcesLoading ? 'Loading…' : 'Pick a resource'}
              </option>
              {resources.map((r) => (
                <option key={r.name} value={r.name}>
                  {r.name} ({r.type})
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Comparator"
            htmlFor="quota-comparator"
            help="Only ≤ is enforced today (= is reserved)."
          >
            <select
              id="quota-comparator"
              value="<="
              disabled
              className={cn(selectClass(false), 'bg-muted/40 cursor-not-allowed')}
            >
              <option>≤</option>
            </select>
          </Field>
          <Field
            label="Quota"
            htmlFor="quota-value"
            required
            error={quotaValueState.error ?? null}
            hint={quotaValueState.okHint ?? null}
            help={
              selectedResource?.type === 'Memory'
                ? 'Accepts human units (1GB, 512MiB) or raw bytes.'
                : selectedResource?.type === 'Double'
                  ? 'Non-negative number. Fractional values allowed.'
                  : 'Non-negative integer.'
            }
          >
            <input
              id="quota-value"
              type="text"
              inputMode={
                selectedResource?.type === 'Double' ? 'decimal' : 'numeric'
              }
              value={values.quota}
              onChange={(e) => set('quota', e.target.value)}
              placeholder={
                selectedResource?.type === 'Memory'
                  ? '64GiB'
                  : selectedResource?.type === 'Double'
                    ? '3.5'
                    : '10'
              }
              className={inputClass(!!quotaValueState.error)}
              disabled={submitting || !selectedResource}
            />
          </Field>
        </div>
        {stringSelected && (
          <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">String resources ignore quotas</div>
              <div className="mt-0.5 opacity-90">
                String resources are non-allocatable, so the server accepts a
                quota but never enforces it. Pick a different resource type if
                you intend the quota to take effect.
              </div>
            </div>
          </div>
        )}
      </Section>

      {submitError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">
              {isCreate ? 'Failed to create quota' : 'Failed to save changes'}
            </div>
            <div className="text-xs mt-0.5 opacity-90">{submitError}</div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Link
          to={cancelHref}
          className="inline-flex items-center px-4 py-2 rounded-md text-sm border hover:bg-muted transition-colors"
        >
          Cancel
        </Link>
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {isCreate ? 'Create quota' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}
